import { matchPath } from "react-router-dom";
import { IBreadcrumbItem } from "dash-ui";
import camelCase from "lodash/camelCase";

import { RoutesPath } from "flow-components/routes/routes";
import {
  IBreadcrumbScopeBuildParams,
  IBreadcrumbScopeBuildHookParams,
} from "common/hooks/use-breadcrumb-builder/breadcrumbs-scope-builders/";
import {
  useGetFeaturedPortfolioByIdLazyQuery,
  useGetFeaturedPropertyByIdLazyQuery,
} from "gql/generated.graphql";
import {
  DEFAULT_FEATURED_PORTFOLIO_TAB_KEY,
  FeaturedPortfolioTabsKeys,
} from "pages/featured-portfolios/components/FeaturedContentTabs/FeaturedContentTabs.constants";

export const useFeaturedPortfoliosBreadcrumbBuilder = (): [
  IBreadcrumbScopeBuildHookParams
] => {
  const [getFeaturedPortfolioById] = useGetFeaturedPortfolioByIdLazyQuery();
  const [getFeaturedPropertyById] = useGetFeaturedPropertyByIdLazyQuery();

  return [
    async ({ pathname, t, currentTab }: IBreadcrumbScopeBuildParams) => {
      const items: IBreadcrumbItem[] = [];
      const featuredContentPath = RoutesPath.FeaturedContent;

      items.push({
        label: t`featuredProperties`,
        path: featuredContentPath,
        icon: "star",
      });

      // add tab breadcrumb when on Featured Portfolios page with a specific tab
      if (
        currentTab &&
        currentTab !== DEFAULT_FEATURED_PORTFOLIO_TAB_KEY &&
        pathname.includes(featuredContentPath)
      ) {
        items.push({
          label: t(camelCase(currentTab)),
          path: `${featuredContentPath}?tab=${currentTab}`,
        });
      }

      // get featured portfolio details path
      const featuredPortfolioParams: any = matchPath(
        RoutesPath.FeaturedContentPortfoliosDetails,
        pathname
      );
      const featuredPortfolioId = featuredPortfolioParams?.params?.id;

      if (featuredPortfolioId) {
        const portfolioPath =
          RoutesPath.FeaturedContentPortfoliosDetails.replace(
            ":id",
            featuredPortfolioId
          );
        const { data } = await getFeaturedPortfolioById({
          variables: { featuredPortfolioId },
        });
        const portfolioName = data?.getFeaturedPortfolioById?.title;

        items.push({
          label: t(camelCase(FeaturedPortfolioTabsKeys.PORTFOLIOS)),
          path: `${featuredContentPath}?tab=${FeaturedPortfolioTabsKeys.PORTFOLIOS}`,
        });

        // add portfolio name as breadcrumb
        if (portfolioName) {
          items.push({ label: portfolioName, path: portfolioPath });
        }
      }

      // get featured portfolio details path
      const featuredPropertiesParams: any = matchPath(
        RoutesPath.FeaturedContentPropertiesDetails,
        pathname
      );

      const featuredPropertyId = featuredPropertiesParams?.params?.id;

      if (featuredPropertyId) {
        const propertyPath =
          RoutesPath.FeaturedContentPropertiesDetails.replace(
            ":id",
            featuredPropertyId
          );
        const { data } = await getFeaturedPropertyById({
          variables: { featuredPropertyId },
        });
        const featuredPropertyTitle = data?.getFeaturedPropertyById?.title;

        // add featured property title as breadcrumb
        if (featuredPropertyTitle) {
          items.push({ label: featuredPropertyTitle, path: propertyPath });
        }
      }
      return items;
    },
  ];
};
